import React, { useEffect, useState, useContext, useMemo } from "react";
import { Typography, Box, Backdrop, CircularProgress, InputAdornment, TextField, Button, Stack,
    TableCell, tableCellClasses, styled, MenuItem } from "@mui/material";
import { Search } from '@mui/icons-material';
import Header from "../layout/Header";
import { API } from "../../conf";
import { Context } from "../../App";
import moment from 'moment';
import { snapToSaturday } from '../../utils/dateUtils';
import SortCheckTotalTable from "../ui/sortCheckTotalTable";

export default function JrnDonInterEglise() {
    const { updateConnect, exercice } = useContext(Context)
    const [organisation, setOrganisation] = useState(JSON.parse(sessionStorage.getItem('eglise')))
    const [agent, setAgent] = useState(JSON.parse(sessionStorage.getItem('user')))
    const [eglises, setEglises] = useState([])
    const [egliseId, setEgliseId] = useState('')

    const eglise = useMemo(() => {
        if (organisation && organisation.type === 'église') return organisation
        if (egliseId !== '') return eglises.find(el => el._id === egliseId)
        return undefined
    }, [eglises, egliseId, organisation])

    const StyledTableCell = styled(TableCell)(({ theme }) => ({
        [`&.${tableCellClasses.body}`]: { fontSize: 14 },
    }))

    const columns = [
        {
            header: "Date", sortable: true,
            sortBy: el => new Date(el.date).getTime(),
            row: (row, index) => <StyledTableCell key={index} align="center">{snapToSaturday(row.date).format('DD/MM/YYYY')}</StyledTableCell>
        },
        {
            header: "Église source", sortable: true,
            sortBy: el => el.sourceEglise.toLowerCase(),
            row: (row, index) => <StyledTableCell key={index} align="center" sx={{ minWidth: '160px', textTransform: 'capitalize' }}>{row.sourceEglise}</StyledTableCell>
        },
        {
            header: "Libellé", sortable: true,
            sortBy: el => el.libelle.toLowerCase(),
            row: (row, index) => <StyledTableCell key={index} align="center" sx={{ minWidth: '180px' }}>{row.libelle}</StyledTableCell>
        },
        {
            header: "Compte", sortable: true,
            sortBy: el => el.compte.toLowerCase(),
            row: (row, index) => <StyledTableCell key={index} align="center" sx={{ minWidth: '120px' }}>{row.compte}</StyledTableCell>
        },
        {
            header: "Montant", sortable: true, isNumeric: true,
            sortBy: el => el.montant,
            getValue: row => row.montant,
            formatTotal: value => `${value.toFixed(2)}€`,
            row: (row, index) => <StyledTableCell key={index} align="center">{row.montant.toFixed(2)}€</StyledTableCell>
        },
        {
            header: "N° reçu source", sortable: true,
            sortBy: el => el.recu_num,
            row: (row, index) => <StyledTableCell key={index} align="center" sx={{ minWidth: '110px' }}>{row.recu_num}</StyledTableCell>
        },
    ]

    const [loading, setLoad] = useState(true)
    const [start_date, setStart] = useState(moment().startOf('year').format('YYYY-MM-DD'))
    const [end_date, setEnd] = useState(moment().format('YYYY-MM-DD'))
    const [search_input, setSearch] = useState('')
    const [rows, setRows] = useState([])
    const [selectItems, setItems] = useState([])

    function normalize(str) {
        return str.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9\s-.€]/gi, '').toLowerCase()
    }

    function getDons(endDate = end_date, startDate = start_date) {
        if (!eglise) return
        setLoad(true)
        const params = JSON.stringify({ eglise: eglise.number, start: new Date(startDate), end: new Date(endDate), exercice })
        fetch(API.concat(`/dons/inter-eglise/${params}`), {
            method: 'GET',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + sessionStorage.getItem('token')
            }
        }).then(async response => {
            const token = response.headers.get('x-auth-token')
            if (response.status === 401) { sessionStorage.clear(); updateConnect(false) }
            else {
                if (token && token !== sessionStorage.getItem('token') && token !== '') sessionStorage.setItem('token', token)
                if (response.status === 200) {
                    const json = await response.json()
                    // Aplatir : une ligne par offrande spécifiée ciblant ce compte
                    const flat = json.flatMap(don =>
                        [...don.offrande_spe_eglise, ...don.offrande_spe_feam].map(o => ({
                            _id: don._id + o.compte,
                            date: don.date,
                            sourceEglise: don.sourceEglise,
                            libelle: o.libelle || '—',
                            compte: o.compte,
                            montant: o.montant,
                            recu_num: don.recu_num,
                        }))
                    )
                    setRows(flat)
                }
                setLoad(false)
            }
        })
    }

    useEffect(() => {
        setOrganisation(JSON.parse(sessionStorage.getItem('eglise')))
        setAgent(JSON.parse(sessionStorage.getItem('user')))
    }, [])

    useEffect(() => {
        if (organisation?.type === 'fédération') {
            setLoad(true)
            fetch(API.concat(`/eglises/by/${JSON.stringify(agent.eglises?.length > 0 ? { number: { $in: agent.eglises } } : { type: 'église' })}`), {
                method: 'GET',
                headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + sessionStorage.getItem('token') }
            }).then(async response => {
                const token = response.headers.get('x-auth-token')
                if (response.status === 401) { sessionStorage.clear(); updateConnect(false) }
                else {
                    if (token && token !== sessionStorage.getItem('token') && token !== '') sessionStorage.setItem('token', token)
                    if (response.status === 200) { setEglises(await response.json()) }
                    setLoad(false)
                }
            })
        }
    }, [agent, organisation])

    useEffect(() => {
        if (eglise?.number) getDons()
    }, [eglise])

    const filtered = rows.filter(row =>
        normalize(`${row.sourceEglise} ${row.libelle} ${row.compte} ${row.recu_num}`).includes(normalize(search_input))
    )

    return (
        <div style={{ display: 'flex', flexDirection: 'column', paddingBottom: '30px' }}>
            <Header />
            <Typography sx={{ textAlign: 'center', marginTop: '20px', fontSize: 26 }}>Dons inter-église reçus</Typography>

            {organisation?.type === 'fédération' && (
                <TextField select size="small" label="Église" sx={{ alignSelf: 'end', marginBottom: 3, width: '25%', marginRight: 3, minWidth: '200px' }}
                    value={egliseId} onChange={e => { setEgliseId(old => { if (old !== e.target.value) setRows([]); return e.target.value }) }}>
                    {eglises.sort((a, b) => a.nom.toLowerCase() < b.nom.toLowerCase() ? -1 : 1)
                        .map(eg => <MenuItem key={eg._id} value={eg._id}>{`${eg.nom} - ${eg.commune.toUpperCase()}`}</MenuItem>)}
                </TextField>
            )}

            <Box sx={{ paddingX: '20px', marginTop: '30px' }}>
                <Stack direction='row' spacing={2} alignItems='center' mb={3}>
                    <Typography fontWeight={600} fontSize={16}>Période :</Typography>
                    <TextField size="small" margin="normal" sx={{ minWidth: '90px' }} InputLabelProps={{ shrink: true }}
                        label="Date de début" type='date' value={start_date}
                        inputProps={{ max: new Date().toISOString().split("T")[0] }}
                        onChange={e => { setStart(e.target.value); if (end_date !== '' && new Date(end_date) <= new Date(e.target.value)) setEnd('') }}
                    />
                    <TextField size="small" margin="normal" sx={{ minWidth: '90px' }} InputLabelProps={{ shrink: true }}
                        label="Date de fin" type='date' value={end_date}
                        inputProps={{ max: new Date().toISOString().split("T")[0] }}
                        onChange={e => { setEnd(e.target.value); if (start_date !== '' && new Date(e.target.value) <= new Date(start_date)) setStart('') }}
                    />
                    <Button variant="contained" disabled={start_date === '' || end_date === '' || !eglise}
                        onClick={() => getDons(end_date, start_date)}>Appliquer</Button>
                </Stack>

                <TextField size="small" label="Rechercher (église, libellé, compte, n° reçu)"
                    disabled={rows.length === 0} sx={{ width: '60%', minWidth: '300px', marginBottom: 2 }}
                    value={search_input}
                    InputProps={{ endAdornment: <InputAdornment position="end"><Search /></InputAdornment> }}
                    onChange={e => setSearch(e.target.value)}
                />

                {!loading && rows.length === 0 && (
                    <Typography color="text.secondary" sx={{ mt: 2 }}>
                        Aucun don inter-église reçu sur cette période.
                    </Typography>
                )}

                {!loading && filtered.length > 0 && (
                    <SortCheckTotalTable data={filtered} table={columns} rowsPerPage={50} rowsPerPageOptions={[10, 25, 50, 100]} selectItems={selectItems} setItems={setItems} />
                )}
            </Box>

            <Backdrop open={loading}><CircularProgress color="inherit" /></Backdrop>
        </div>
    )
}
